/**
 * Connector Import hooks — lazy tree browsing + import start.
 *
 * Reads:
 *  - useConnectorTree: lazy children of a tree node (or roots when parentRef is absent)
 *
 * Mutations:
 *  - useStartConnectorImport: POST import → 202 + runId
 *
 * Socket:
 *  - useConnectorImportTraySocket: app-level subscription on the /notifications
 *    namespace that feeds the global upload-manager tray. Mounted once near the
 *    app root (NOT in the wizard) so per-page rows + their done state survive
 *    the wizard closing. This live socket path is how the user tracks run
 *    progress — there is no run-status polling hook (CR-B4 removed the unused
 *    `useConnectorImportRun`; the backend `GET /connector-import/:runId`
 *    endpoint remains for a future "view run" surface).
 *
 * Query key convention:
 *  - ['connectorTree', toolkitKey, parentRef]  — one key per (toolkit, level)
 */

import { useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';
import { connectorImportService } from '../../services/connectorImport.service';
import type {
  TreeNode,
  ImportSelection,
  ConnectorImportRun,
  ConnectorImportCompletedData,
} from '../../services/connectorImport.service';
import { ApiResponseError, unwrapApiResponse } from '../../services/api/http-client.types';
import { useNamespaceSocket } from '../../contexts/SocketContext';
import {
  useUploadManagerStore,
  type ConnectorImportPageStatus,
  type ConnectorImportPageItem,
} from '../../stores/uploadManagerStore';
import { useConnectorImportRunsStore } from '../../stores/connectorImportRunsStore';
import { useAppToast } from '../ui/useAppToast';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

/**
 * One key per (toolkitKey, parentRef) pair so TanStack Query caches each
 * expanded level independently. `undefined` parentRef = roots.
 */
export const connectorTreeKey = (toolkitKey: string, parentRef: string | undefined) =>
  ['connectorTree', toolkitKey, parentRef ?? null] as const;

// ---------------------------------------------------------------------------
// Re-export types so consumers don't need to reach into the service module
// ---------------------------------------------------------------------------

export type { TreeNode, ImportSelection, ConnectorImportRun };
export type { ConnectorImportCompletedData };

// ---------------------------------------------------------------------------
// useConnectorTree
// ---------------------------------------------------------------------------

/**
 * Fetch the tree children for a connector toolkit at a given level.
 *
 * - Call with no `parentRef` (or `parentRef: undefined`) to fetch root nodes.
 * - Call with a `ref` from a prior `TreeNode` to expand that container's
 *   children lazily.
 * - Pass `options.enabled: false` to defer fetching until the node is expanded;
 *   the wizard toggles this when the user opens a folder.
 *
 * staleTime is set to 2 minutes — source trees are unlikely to change during
 * a single wizard session, but we don't cache them as aggressively as the
 * static toolkit catalog.
 */
export function useConnectorTree(
  toolkitKey: string,
  parentRef: string | undefined,
  options?: { enabled?: boolean },
): ReturnType<typeof useQuery<TreeNode[]>> {
  const enabled = options?.enabled ?? true;

  return useQuery<TreeNode[]>({
    queryKey: connectorTreeKey(toolkitKey, parentRef),
    queryFn: async () => {
      // `unwrapApiResponse` throws `ApiResponseError` carrying `code` AND
      // `status`, which is what lets the wizard pick a remedy: the tree's
      // failure copy is chosen from both, and a bare `Error` would throw them
      // away and leave one hardcoded "check your connection" string for every
      // cause. It does NOT validate data presence (its own doc says so), so a
      // body-less success still needs the explicit check below.
      const data = unwrapApiResponse(
        await connectorImportService.listTree(toolkitKey, parentRef),
        'Failed to load connector tree',
      );
      if (!data) {
        throw new ApiResponseError('Failed to load connector tree', undefined);
      }
      return data;
    },
    enabled,
    staleTime: 2 * 60 * 1000,
  } satisfies UseQueryOptions<TreeNode[]>);
}

// ---------------------------------------------------------------------------
// useStartConnectorImport
// ---------------------------------------------------------------------------

/**
 * Mutation: POST to start a connector import run.
 *
 * On success: returns `{ runId }`. On failure: rejects with an
 * `ApiResponseError` carrying the backend error `code` + HTTP `status` so the
 * wizard can show mapped, human-readable copy.
 *
 * Analytics are emitted by the BACKEND only (CR-A3 — single source of truth):
 * `startImport` fires `Connector Import Started`, so the frontend deliberately
 * does NOT, to avoid double-counting with a divergent prop schema.
 */
export function useStartConnectorImport(toolkitKey: string) {
  const startRun = useConnectorImportRunsStore((s) => s.startRun);
  return useMutation({
    mutationFn: async (payload: {
      selectedNodes: ImportSelection[];
      targetCollectionId?: string;
    }) => {
      const data = unwrapApiResponse(
        await connectorImportService.startImport(toolkitKey, payload),
        'Failed to start import',
      );
      if (!data) {
        throw new ApiResponseError('Failed to start import', undefined);
      }
      return data; // { runId }
    },
    // Mark the run in progress so the source's Import button shows a loader
    // until the app-level tray socket sees this run's `completed` event. The
    // run id ties the two together (started here, cleared there).
    onSuccess: (data) => {
      if (data?.runId) startRun(data.runId, toolkitKey);
    },
  });
}

// ---------------------------------------------------------------------------
// useConnectorImportTraySocket (app-level)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Per-item socket event payload types
// ---------------------------------------------------------------------------

interface ConnectorImportItemQueuedData {
  readonly runId: string;
  readonly itemId: string;
  readonly name: string;
}

interface ConnectorImportItemDoneData {
  readonly runId: string;
  readonly itemId: string;
  readonly name: string;
  readonly status: 'SUCCEEDED' | 'SKIPPED' | 'FAILED';
  readonly reason?: string;
}

// ---------------------------------------------------------------------------
// Per-item event handlers (extracted to keep handleNotification under
// sonarjs/cognitive-complexity limit of 20)
// ---------------------------------------------------------------------------

const ITEM_DONE_STATUS_MAP: Record<string, ConnectorImportPageStatus> = {
  SUCCEEDED: 'done',
  SKIPPED: 'skipped',
  FAILED: 'error',
};

function handleItemQueued(
  data: Record<string, unknown> | undefined,
  registerImportPage: (item: Omit<ConnectorImportPageItem, 'kind'>) => void,
): void {
  const payload = data as ConnectorImportItemQueuedData | undefined;
  if (!payload?.itemId || !payload.runId) return;
  registerImportPage({
    id: payload.itemId,
    runId: payload.runId,
    pageName: payload.name,
    status: 'pending',
  });
}

function handleItemDone(
  data: Record<string, unknown> | undefined,
  updateImportPage: (id: string, status: ConnectorImportPageStatus, error?: string) => void,
): void {
  const payload = data as ConnectorImportItemDoneData | undefined;
  if (!payload?.itemId) return;
  const trayStatus: ConnectorImportPageStatus = ITEM_DONE_STATUS_MAP[payload.status] ?? 'error';
  const errorMsg = payload.status === 'FAILED' ? (payload.reason ?? 'Import failed') : undefined;
  updateImportPage(payload.itemId, trayStatus, errorMsg);
}

function handleRunCompleted(
  data: Record<string, unknown> | undefined,
  endRun: (runId: string) => void,
  toast: ReturnType<typeof useAppToast>,
): void {
  const payload = data as ConnectorImportCompletedData | undefined;
  if (!payload?.runId) return;

  // Clear the source's Import-button loader regardless of outcome.
  endRun(payload.runId);

  // A FAILED run (e.g. discovery couldn't complete — the exact case that made a
  // re-import look like "nothing happened") has no per-page tray rows to carry
  // the error, so surface it as a toast. Success + partial failures already
  // show in the tray, so we don't double-signal those.
  if (payload.status === 'FAILED') {
    toast({
      title: 'Import failed',
      description: "The import couldn't be completed. Nothing was imported: please try again.",
      status: 'error',
    });
  }
}

/**
 * App-level subscription that feeds the global background-tasks tray with
 * connector-import progress. Mount it ONCE near the app root (alongside
 * `useBillingRealtime` / `useNotificationSocket`) — NOT inside the import
 * wizard.
 *
 * This is the fix for the tray never showing per-page progress: the wizard
 * unmounts the moment the user clicks Import, so any subscription it hosts dies
 * before the `item-queued` / `item-done` events arrive. Lifting the listener to
 * the app root keeps it alive for the whole session, so rows appear as pages
 * import and settle to their done state even though the wizard has closed.
 *
 * No runId argument is needed: the backend emits every connector-import event
 * via `notifyUser(userId, …)`, so the `/notifications` socket only ever
 * delivers the current user's runs. The `runId` carried on each event keys the
 * tray row; this listener consumes whichever run is active.
 *
 * Pattern mirrors `useBillingRealtime` and `useOnboardingChecklist` — same
 * `useNamespaceSocket` + `socket.on`/`socket.off` in `useEffect` idiom.
 */
export function useConnectorImportTraySocket(): void {
  const socket = useNamespaceSocket('notifications');
  const registerImportPage = useUploadManagerStore((s) => s.registerImportPage);
  const updateImportPage = useUploadManagerStore((s) => s.updateImportPage);
  const endRun = useConnectorImportRunsStore((s) => s.endRun);
  const toast = useAppToast();

  useEffect(() => {
    if (!socket) return;

    /**
     * NotificationEvent wrapper shape (from notifications.namespace.ts):
     *  { type: 'connector-import:item-queued', data: ConnectorImportItemQueuedData, … }
     *  { type: 'connector-import:item-done', data: ConnectorImportItemDoneData, … }
     *  { type: 'connector-import:completed', data: ConnectorImportCompletedData, … }
     *
     * The backend emits all event types on the shared `notification` socket
     * event, so we listen on `notification` and dispatch by `event.type`.
     */
    const handleNotification = (event: { type: string; data?: Record<string, unknown> }) => {
      if (event.type === 'connector-import:item-queued') {
        handleItemQueued(event.data, registerImportPage);
        return;
      }
      if (event.type === 'connector-import:item-done') {
        handleItemDone(event.data, updateImportPage);
        return;
      }
      if (event.type === 'connector-import:completed') {
        // Clears the source's Import-button loader, and toasts a run-level
        // FAILED (the silent-failure gap). Per-page rows already settled via
        // `item-done`; run-level analytics are still backend-only (CR-A3).
        handleRunCompleted(event.data, endRun, toast);
        return;
      }
    };

    socket.on('notification', handleNotification);
    return () => {
      socket.off('notification', handleNotification);
    };
  }, [socket, registerImportPage, updateImportPage, endRun, toast]);
}
